import { MailOutlined, UserOutlined } from '@ant-design/icons'
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Input,
	Popconfirm,
	Radio,
	Select,
	Space,
	Tag,
	Typography,
} from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import api from '../../api/index.api'
import { ButtonCommon, ModalCommon } from '../../common'
import { ListPageIntro, ListPageShell } from '../../common/ListPageLayout/ListPageLayout'
import { TextAreaCommon } from '../../common/TextAreaCommon/TextAreaCommon'
import { showMessageError, showMessageSuccess } from '../../utils/function.utils'
import './EmailSend.scss'

const { Text } = Typography

const JOB_STATUS_COLORS = {
	queued: 'gold',
	running: 'processing',
	completed: 'success',
	failed: 'error',
	cancelled: 'default',
}

export const EmailSend = () => {
	const location = useLocation()
	const pollTimerRef = useRef(null)
	const inputRef = useRef(null)

	const presetTemplateId = useMemo(() => {
		const raw = new URLSearchParams(location.search).get('templateId')
		const n = Number(raw)
		return Number.isFinite(n) && n > 0 ? n : null
	}, [location.search])

	const [sesStatus, setSesStatus] = useState(null)
	const [templates, setTemplates] = useState([])
	const [templateId, setTemplateId] = useState(presetTemplateId)
	const [targetType, setTargetType] = useState(1)
	const [userEmail, setUserEmail] = useState('')
	const [subject, setSubject] = useState('')
	const [htmlBody, setHtmlBody] = useState('')
	const [textBody, setTextBody] = useState('')
	const [emailType, setEmailType] = useState('transactional')
	const [isSubmitLoading, setIsSubmitLoading] = useState(false)
	const [isConfirmVisible, setIsConfirmVisible] = useState(false)
	const [pendingPayload, setPendingPayload] = useState(null)
	const [confirmViewMode, setConfirmViewMode] = useState('json')
	const [sendJobStatus, setSendJobStatus] = useState(null)
	const [isCancellingJob, setIsCancellingJob] = useState(false)
	const [estimate, setEstimate] = useState(null)
	const [excludeAlreadySent, setExcludeAlreadySent] = useState(false)

	const stopPolling = () => {
		if (pollTimerRef.current) {
			clearTimeout(pollTimerRef.current)
			pollTimerRef.current = null
		}
	}

	useEffect(() => {
		;(async () => {
			try {
				const [status, tplRs] = await Promise.all([
					api.emailAdminApi.getSESStatus(),
					api.emailAdminApi.getTemplates({
						page: 0,
						pageSize: 100,
						status: 'active',
					}),
				])
				setSesStatus(status)
				setTemplates(Array.isArray(tplRs?.data) ? tplRs.data : [])
			} catch (err) {
				showMessageError(err?.message || 'Failed to load email send prerequisites.')
			}

			// Restore Last send job card after refresh if a job is still active.
			try {
				let active = null
				for (const jobStatus of ['running', 'queued']) {
					const rs = await api.emailAdminApi.listSendJobs({
						page: 0,
						pageSize: 1,
						status: jobStatus,
					})
					const newest = Array.isArray(rs?.data) ? rs.data[0] : null
					if (newest?.jobID) {
						active = newest
						break
					}
				}
				if (active?.jobID) {
					const detail =
						(await api.emailAdminApi.getSendJobStatus(active.jobID)) || active
					setSendJobStatus(detail)
					if (['queued', 'running'].includes(detail.status)) {
						pollSendJobStatus(detail.jobID)
					}
				}
			} catch {
				// Non-blocking: Send form still works without resume.
			}
		})()
		return () => stopPolling()
	}, [])

	useEffect(() => {
		if (!templateId) return
		const tpl = templates.find(item => item.id === templateId)
		if (!tpl) return
		setSubject(tpl.subject || '')
		setHtmlBody(tpl.htmlBody || '')
		setTextBody(tpl.textBody || '')
		setEmailType(tpl.emailType || 'transactional')
	}, [templateId, templates])

	const buildPayload = (dryRun = false) => {
		const payload = {
			subject: subject.trim(),
			htmlBody: htmlBody.trim(),
			textBody: textBody.trim(),
			emailType,
			dryRun,
		}
		if (templateId) payload.templateId = templateId
		if (targetType === 2) {
			payload.targetEmail = userEmail.trim().toLowerCase()
		} else if (excludeAlreadySent) {
			payload.excludeAlreadySent = true
		}
		return payload
	}

	const validateBeforeSend = () => {
		if (!subject.trim()) {
			showMessageError('Subject is required.')
			return false
		}
		if (!htmlBody.trim() && !textBody.trim()) {
			showMessageError('HTML or text body is required.')
			return false
		}
		if (targetType === 2 && !userEmail.trim()) {
			showMessageError('Please enter user email for specific-user mode.')
			return false
		}
		return true
	}

	const pollSendJobStatus = jobID => {
		api.emailAdminApi
			.getSendJobStatus(jobID)
			.then(rs => {
				setSendJobStatus(rs)
				if (rs.status === 'completed') {
					showMessageSuccess(
						`Email job completed (sent ${rs.result?.emailsSent || 0}, failed ${rs.result?.emailsFailed || 0}, skipped ${rs.result?.emailsSkipped || 0}).`
					)
					stopPolling()
					return
				}
				if (rs.status === 'failed') {
					showMessageError(rs.error || 'Email send job failed.')
					stopPolling()
					return
				}
				if (rs.status === 'cancelled') {
					showMessageSuccess('Email send job cancelled.')
					stopPolling()
					return
				}
				pollTimerRef.current = setTimeout(() => pollSendJobStatus(jobID), 2000)
			})
			.catch(err => {
				showMessageError(err?.message || 'Failed to poll job status.')
				stopPolling()
			})
	}

	const handleDryRun = async () => {
		if (!validateBeforeSend()) return
		setIsSubmitLoading(true)
		try {
			const rs = await api.emailAdminApi.sendEmail(buildPayload(true))
			setEstimate(rs?.estimate || null)
			showMessageSuccess(
				`Dry run OK. Estimated recipients: ${rs?.estimate?.estimatedRecipients ?? 0}. Transport: ${rs?.transport || (rs?.sesConfigured ? 'ses' : 'none')}.`
			)
		} catch (err) {
			showMessageError(err?.message || 'Dry run failed.')
		} finally {
			setIsSubmitLoading(false)
		}
	}

	const canSend =
		sesStatus?.configured === true ||
		sesStatus?.transport === 'smtp' ||
		sesStatus?.transport === 'ses'

	const openConfirmModal = () => {
		if (!validateBeforeSend()) return
		if (sesStatus && !canSend) {
			showMessageError(
				sesStatus.adminSendViaSMTP
					? 'SMTP is not configured on the backend yet (common.email email/password/smtp/port).'
					: 'AWS SES is not configured on the backend yet. Or set common.email.adminSendViaSMTP=true to use SMTP (e.g. Zoho).'
			)
			return
		}
		setPendingPayload(buildPayload(false))
		setIsConfirmVisible(true)
	}

	const submitConfirmedPayload = async () => {
		if (!pendingPayload) return
		setIsSubmitLoading(true)
		setIsConfirmVisible(false)
		try {
			const rs = await api.emailAdminApi.sendEmail(pendingPayload)
			if (rs?.jobID) {
				setSendJobStatus(rs)
				setEstimate(rs.estimate || null)
				showMessageSuccess(`Send job queued (${rs.jobID}). Tracking progress.`)
				stopPolling()
				pollSendJobStatus(rs.jobID)
			} else {
				showMessageSuccess(
					`Email sent (sent ${rs?.emailsSent || 0}, skipped ${rs?.emailsSkipped || 0}).`
				)
			}
			setUserEmail('')
		} catch (err) {
			showMessageError(err?.message || 'Send failed.')
		} finally {
			setIsSubmitLoading(false)
		}
	}

	const audienceSummary =
		targetType === 2
			? `Specific user: ${userEmail.trim() || '(missing email)'}`
			: excludeAlreadySent
				? 'All eligible users (exclude already successfully sent)'
				: 'All eligible users'

	return (
		<ListPageShell className="email-send">
			<div className="list-page-bar">
				<ListPageIntro
					icon={MailOutlined}
					title="Send email"
					subtitle="Send via AWS SES (or SMTP fallback) to one user or all eligible users. Marketing/newsletter respect email_consent. Past sends are in Send history."
				/>
			</div>

			{sesStatus && sesStatus.transport === 'smtp' && (
				<Alert
					type="info"
					showIcon
					className="email-send__alert"
					message="Sending via SMTP"
					description={`From ${sesStatus.fromEmail || 'common.email.email'}. SMTP providers often enforce daily recipient limits and may block templated marketing — test one address first. SES delivery webhooks do not apply.`}
				/>
			)}

			{sesStatus && !canSend && (
				<Alert
					type="warning"
					showIcon
					className="email-send__alert"
					message="No email transport configured"
					description={
						sesStatus.adminSendViaSMTP
							? 'adminSendViaSMTP is on, but SMTP credentials are missing (common.email email/password/smtp/port).'
							: 'Enable common.email.ses (enabled, region, keys, fromEmail), or set common.email.adminSendViaSMTP=true to use SMTP (e.g. Zoho).'
					}
				/>
			)}

			{sesStatus && sesStatus.transport === 'ses' && !sesStatus.hasConfigurationSet && (
				<Alert
					type="info"
					showIcon
					className="email-send__alert"
					message="Delivery tracking not fully wired"
					description="Set common.email.ses.configurationSet and subscribe SNS to POST /api/webhooks/ses/email-events to record delivery/bounce/complaint on Send history."
				/>
			)}

			<Card className="email-send__card" bordered>
				<Space direction="vertical" size={16} className="email-send__form">
					<div>
						<div className="email-send__label">Template (optional)</div>
						<Select
							allowClear
							placeholder="Select an active template"
							style={{ width: '100%' }}
							value={templateId || undefined}
							onChange={value => setTemplateId(value || null)}
							options={templates.map(tpl => ({
								value: tpl.id,
								label: `${tpl.name} (${tpl.emailType})`,
							}))}
						/>
					</div>

					<div>
						<div className="email-send__label">
							Email type<span className="email-send__required">*</span>
						</div>
						<Select
							style={{ width: '100%' }}
							value={emailType}
							onChange={setEmailType}
							options={[
								{ value: 'transactional', label: 'Transactional' },
								{ value: 'marketing', label: 'Marketing' },
								{ value: 'newsletter', label: 'Newsletter' },
								{ value: 'other', label: 'Other' },
							]}
						/>
						<Text type="secondary" className="email-send__hint">
							Marketing/newsletter only go to users with email consent.
						</Text>
					</div>

					<div>
						<div className="email-send__label">
							Subject<span className="email-send__required">*</span>
						</div>
						<Input
							value={subject}
							onChange={e => setSubject(e.target.value)}
							placeholder="Subject"
							maxLength={998}
						/>
					</div>

					<div>
						<div className="email-send__label">HTML body</div>
						<TextAreaCommon
							showCount
							value={htmlBody}
							onChange={setHtmlBody}
							placeholder="<p>Hello...</p>"
							maxLength={200000}
						/>
					</div>

					<div>
						<div className="email-send__label">Text body</div>
						<TextAreaCommon
							showCount
							value={textBody}
							onChange={setTextBody}
							placeholder="Plain text fallback"
							maxLength={100000}
						/>
					</div>

					<div>
						<div className="email-send__label">Audience</div>
						<Radio.Group
							value={targetType}
							onChange={e => {
								setTargetType(e.target.value)
								if (e.target.value === 2) {
									inputRef.current?.focus()
									setExcludeAlreadySent(false)
								}
							}}
						>
							<Space direction="vertical" size={8}>
								<Radio value={1}>All eligible users</Radio>
								<Radio value={2}>Specific user (by email)</Radio>
							</Space>
						</Radio.Group>
						{targetType === 1 ? (
							<div style={{ marginTop: 8 }}>
								<Checkbox
									checked={excludeAlreadySent}
									onChange={e => setExcludeAlreadySent(e.target.checked)}
								>
									Exclude users already successfully sent this campaign
								</Checkbox>
								<Text type="secondary" className="email-send__hint">
									Skips users with status sent/delivered/bounced/complained for
									the same template (or same subject if no template). Failed
									attempts (e.g. SES sandbox) stay eligible. Prefer resuming the
									same unfinished job when possible.
								</Text>
							</div>
						) : null}
					</div>

					<div>
						<div className="email-send__label">
							User email
							{targetType === 2 && (
								<span className="email-send__required">*</span>
							)}
						</div>
						<Input
							ref={inputRef}
							disabled={targetType !== 2}
							value={userEmail}
							onChange={e => setUserEmail(e.target.value)}
							prefix={<UserOutlined />}
							placeholder="example@domain.com"
						/>
					</div>

					{estimate ? (
						<Alert
							type="info"
							showIcon
							message={`Last estimate: ${estimate.estimatedRecipients ?? 0} recipients (${estimate.scope})`}
						/>
					) : null}

					<div className="email-send__actions">
						<ButtonCommon
							type="primary"
							onClick={openConfirmModal}
							loading={isSubmitLoading}
						>
							Send email
						</ButtonCommon>
						<Button onClick={handleDryRun} disabled={isSubmitLoading}>
							Dry run (no send)
						</Button>
					</div>
				</Space>
			</Card>

			<ModalCommon
				title="Confirm email send"
				visible={isConfirmVisible}
				handleCancel={() => setIsConfirmVisible(false)}
				width={760}
				footer={
					<div className="email-send__confirm-footer">
						<Button onClick={() => setIsConfirmVisible(false)}>Cancel</Button>
						<Button
							type="primary"
							danger
							onClick={submitConfirmedPayload}
							loading={isSubmitLoading}
						>
							Confirm send
						</Button>
					</div>
				}
			>
				<div className="email-send__confirm-meta">
					<div>
						<strong>Type:</strong> {emailType}
					</div>
					<div>
						<strong>Audience:</strong> {audienceSummary}
					</div>
					<div>
						<strong>Subject:</strong> {subject.trim() || '—'}
					</div>
				</div>
				<div className="email-send__confirm-view-toggle">
					<Button
						size="small"
						type={confirmViewMode === 'json' ? 'primary' : 'default'}
						onClick={() => setConfirmViewMode('json')}
					>
						Payload JSON
					</Button>
					<Button
						size="small"
						type={confirmViewMode === 'html' ? 'primary' : 'default'}
						onClick={() => setConfirmViewMode('html')}
					>
						HTML preview
					</Button>
				</div>
				{confirmViewMode === 'html' ? (
					<div
						className="email-send__confirm-html-preview"
						dangerouslySetInnerHTML={{
							__html: htmlBody.trim() || '<em>—</em>',
						}}
					/>
				) : (
					<pre className="email-send__preview-json">
						{pendingPayload ? JSON.stringify(pendingPayload, null, 2) : '{}'}
					</pre>
				)}
			</ModalCommon>

			{sendJobStatus && (
				<Card className="email-send__job-card" bordered>
					<div className="email-send__job-header">
						<div className="email-send__label">Last send job</div>
						<div className="email-send__job-actions">
							<Button
								size="small"
								onClick={() => {
									if (!sendJobStatus?.jobID) return
									stopPolling()
									pollSendJobStatus(sendJobStatus.jobID)
								}}
								disabled={['completed', 'failed', 'cancelled'].includes(
									sendJobStatus.status
								)}
							>
								Refresh
							</Button>
							<Popconfirm
								title="Cancel this send job? Sending will stop after the current recipient."
								okText="Cancel job"
								cancelText="Keep sending"
								okButtonProps={{ danger: true, loading: isCancellingJob }}
								disabled={['completed', 'failed', 'cancelled'].includes(
									sendJobStatus.status
								)}
								onConfirm={async () => {
									if (!sendJobStatus?.jobID) return
									setIsCancellingJob(true)
									try {
										const rs = await api.emailAdminApi.cancelSendJob(
											sendJobStatus.jobID
										)
										setSendJobStatus(rs)
										showMessageSuccess('Cancel request sent.')
										stopPolling()
									} catch (err) {
										showMessageError(err?.message || 'Cancel failed.')
									} finally {
										setIsCancellingJob(false)
									}
								}}
							>
								<Button
									size="small"
									danger
									loading={isCancellingJob}
									disabled={['completed', 'failed', 'cancelled'].includes(
										sendJobStatus.status
									)}
								>
									Cancel job
								</Button>
							</Popconfirm>
						</div>
					</div>
					<div className="email-send__job-grid">
						<div>
							<strong>Job ID:</strong>{' '}
							<Text
								copyable={{
									text: sendJobStatus.jobID,
									tooltips: ['Copy job ID', 'Copied'],
								}}
							>
								{sendJobStatus.jobID}
							</Text>
						</div>
						<div>
							<strong>Status:</strong>{' '}
							<Tag color={JOB_STATUS_COLORS[sendJobStatus.status] || 'default'}>
								{(sendJobStatus.status || 'unknown').toUpperCase()}
							</Tag>
						</div>
						<div>
							<strong>Scope:</strong> {sendJobStatus.scope}
						</div>
						{sendJobStatus.result ? (
							<>
								<div>
									<strong>Scanned:</strong> {sendJobStatus.result.usersScanned}
								</div>
								<div>
									<strong>Sent:</strong> {sendJobStatus.result.emailsSent}
								</div>
								<div>
									<strong>Failed:</strong> {sendJobStatus.result.emailsFailed}
								</div>
								<div>
									<strong>Skipped:</strong> {sendJobStatus.result.emailsSkipped}
								</div>
							</>
						) : null}
						{sendJobStatus.error ? (
							<div className="email-send__job-error">
								<strong>Error:</strong> {sendJobStatus.error}
							</div>
						) : null}
					</div>
				</Card>
			)}
		</ListPageShell>
	)
}
